import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    IconButton,
    Card,
    Stack,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    CircularProgress,
    Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { initializeDB } from '../../config/db';
import { JHA_STORE, STATUS_NEW } from '../../config/constants';
import { createJhaSkeleton } from './utils/jhaRecordFactory';
import { generateUUID } from '../../utils/uuid';
import { localDateString } from '../../utils/localDate';
import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    ContentCopy as CopyIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    HourglassEmpty as ProcessIcon,
    Drafts as DraftIcon,
    Save as SaveIcon,
    CloudOff as CloudOffIcon,
    Cloud as CloudIcon,
    Sync as SyncIcon,
    Email as EmailIcon,
    MarkEmailRead as EmailSentIcon
} from '@mui/icons-material';
import { startOfWeek, endOfWeek, subWeeks, isWithinInterval, format, isSameWeek } from 'date-fns';
import jhaSyncService from '../../services/jhaSyncService';
import axiosInstance from '../../axiosInstance';
import Swal from 'sweetalert2';

const statusConfig = {
    draft: { label: 'Draft', color: '#9e9e9e', icon: DraftIcon },
    saved: { label: 'Saved', color: '#2196f3', icon: SaveIcon },
    process: { label: 'In Process', color: '#ff9800', icon: ProcessIcon },
    submitted: { label: 'Submitted', color: '#ff9800', icon: ProcessIcon },
    finish: { label: 'Completed', color: '#4caf50', icon: CheckCircleIcon }
};

// Helper function to parse date string as local date to avoid timezone issues
function parseLocalDate(dateString) {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length === 3) {
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
    return new Date(dateString);
}

export default function JHAPage() {
    const [jhaList, setJhaList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
    const [syncing, setSyncing] = useState(false);
    const [sendingEmail, setSendingEmail] = useState({});
    const [emailCooldowns, setEmailCooldowns] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        loadData();
        
        const handleUpdate = () => loadData();
        window.addEventListener('jhaUpdated', handleUpdate);
        window.addEventListener('syncCompleted', handleUpdate);
        
        return () => {
            window.removeEventListener('jhaUpdated', handleUpdate);
            window.removeEventListener('syncCompleted', handleUpdate);
        };
    }, []);

    async function loadData() {
        setLoading(true);
        try {
            const db = await initializeDB();
            const tx = db.transaction(JHA_STORE, 'readonly');
            const store = tx.objectStore(JHA_STORE);
            const allRecords = await store.getAll();
            
            // Normalize status - convert 'completed' to 'finish'
            const normalizedRecords = allRecords.map(record => ({
                ...record,
                status: record.status === 'completed' ? 'finish' : record.status
            }));
            
            setJhaList(normalizedRecords || []);
        } catch (error) {
            console.error('Error loading JHA data:', error);
            setJhaList([]);
        } finally {
            setLoading(false);
        }
    }

    // Filter logic
    const filteredList = useMemo(() => {
        let filtered = [...jhaList];

        // Date filter - only show last week and current week
        const now = new Date();
        const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 }); // Monday
        const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        const currentWeekEnd = endOfWeek(now, { weekStartsOn: 1 });

        // Show if EITHER the form date OR the createdAt falls in the window. Using
        // createdAt as a fallback recovers forms whose stored date landed on the
        // wrong day due to the old UTC-date bug (and is robust to timezones).
        const inWeekWindow = (item) => {
            const dateString = item.stepOne?.date || item.stepTwo?.date;
            const byDate = dateString ? parseLocalDate(dateString) : null;
            const byCreated = item.createdAt ? new Date(item.createdAt) : null;
            const ok = (d) => d && !isNaN(d.getTime()) && isWithinInterval(d, { start: lastWeekStart, end: currentWeekEnd });
            return ok(byDate) || ok(byCreated);
        };
        filtered = filtered.filter(inWeekWindow);

        // Status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(item => {
                const normalizedStatus = item.status === 'completed' ? 'finish' : item.status;
                return normalizedStatus === filterStatus;
            });
        }

        // Sort by date (newest first)
        filtered.sort((a, b) => {
            const dateStringA = a.stepOne?.date || a.stepTwo?.date;
            const dateStringB = b.stepOne?.date || b.stepTwo?.date;
            const dateA = dateStringA ? parseLocalDate(dateStringA) : new Date(a.createdAt || 0);
            const dateB = dateStringB ? parseLocalDate(dateStringB) : new Date(b.createdAt || 0);
            return dateB - dateA;
        });

        return filtered;
    }, [jhaList, filterStatus]);

    // Count statuses for badges (only for current and last week)
    const statusCounts = useMemo(() => {
        const counts = { total: 0 };
        const now = new Date();
        const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
        const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        const currentWeekEnd = endOfWeek(now, { weekStartsOn: 1 });

        const ok = (d) => d && !isNaN(d.getTime()) && isWithinInterval(d, { start: lastWeekStart, end: currentWeekEnd });
        jhaList.forEach(item => {
            const dateString = item.stepOne?.date || item.stepTwo?.date;
            const byDate = dateString ? parseLocalDate(dateString) : null;
            const byCreated = item.createdAt ? new Date(item.createdAt) : null;
            if (ok(byDate) || ok(byCreated)) {
                counts.total += 1;
                const status = item.status === 'completed' ? 'finish' : (item.status || 'draft');
                counts[status] = (counts[status] || 0) + 1;
            }
        });
        return counts;
    }, [jhaList]);

    async function handleAdd() {
        const db = await initializeDB();

        // Get current user info
        const employeeId = localStorage.getItem('employeeId');
        const employeeTx = db.transaction('employees', 'readonly');
        const employeeStore = employeeTx.objectStore('employees');
        const allEmployees = await employeeStore.getAll();
        const currentEmployee = allEmployees.find(emp =>
            String(emp.EmployeeId || emp.id || emp.ID) === String(employeeId)
        );
        const defaultPerson = currentEmployee?.FullName || '';

        // Get docs and evaluation
        const [allDocs, allEvaluation] = await Promise.all([
            db.transaction('docsPermit', 'readonly').objectStore('docsPermit').getAll(),
            db.transaction('evaluationOfWork', 'readonly').objectStore('evaluationOfWork').getAll()
        ]);

        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        const newId = generateUUID();
        const timestamp = new Date().toISOString();

        const newRecord = createJhaSkeleton({
            id: newId,
            status: STATUS_NEW,
            createdAt: timestamp,
            defaultPerson,
            docs: allDocs,
            evaluation: allEvaluation,
            today: localDateString(),
            metaLastStep: 1,
        });

        await store.put(newRecord);
        await tx.done;
        window.dispatchEvent(new Event('jhaUpdated'));

        navigate('/jha/step-1', { state: { isNew: false, jha: newRecord } });
    }

    function handleSelectRecord(record) {
        if (record.status === 'finish' || record.status === 'completed' || record.syncStatus === 'synced') {
            navigate(`/jha/view/${record.id}`);
            return;
        }

        const targetStep = Math.min(Math.max(record?.meta?.lastStep || 1, 1), 5);
        const path = `/jha/step-${targetStep}`;
        navigate(path, { state: { isNew: false, jha: record } });
    }

    async function handleCopyRecord(record, e) {
        e.stopPropagation();

        // Allow copying only for records with 'finish' status
        if (record.status !== 'finish' && record.status !== 'completed') {
            return;
        }

        const newId = generateUUID();
        const newRecord = {
            ...record,
            id: newId,
            localId: newId,
            backendId: null,
            syncStatus: null,
            status: 'draft',
            createdAt: new Date().toISOString(),
            submittedAt: null,
            stepOne: {
                ...record.stepOne,
                date: localDateString()
            },
            stepTwo: {
                ...record.stepTwo,
                // Clear documents and their attachments, also clear linkedCsepUuid/linkedLttUuid
                docsPermits: record.stepTwo?.docsPermits?.map(doc => ({
                    ...doc,
                    required: 'Not Req',
                    attachments: [],
                    linkedCsepUuid: null,
                    linkedLttUuid: null
                })) || [],
                customDocsPermits: record.stepTwo?.customDocsPermits?.map(doc => ({
                    ...doc,
                    required: 'Not Req',
                    attachments: [],
                    linkedCsepUuid: null,
                    linkedLttUuid: null
                })) || [],
                // Clear evaluation answers
                evaluation: {}
            },
            stepThree: {
                ...record.stepThree,
                // Clear supervisor signature on step 3
                preShiftSignature: '',
                supervisorSignature: '',
                supervisorSignedAt: null,
                // Clear employee signatures
                employeesSignatures: [],
                // Clear initial submission data
                initialSubmission: null
            },
            stepFour: {
                ...record.stepFour,
                // Clear employee acknowledgments/signatures
                employeeAcknowledgments: []
            },
            stepFive: {
                ...record.stepFive,
                // Clear supervisor signature on step 5
                supervisorSignature: '',
                postShiftSupervisorSignature: '',
                postShiftSupervisorSignedAt: null,
                completedAt: null,
                // Clear checklist answers
                checklist: {},
                checklistNotes: {},
                comments: '',
                // Clear safety department signature (but keep safetyEmployee reference)
                safetySignature: ''
            },
            meta: { lastStep: 1 },
            // Clear email status
            emailSent: false,
            emailSentAt: null,
            emailSentTo: null
        };

        // Save to IndexedDB before navigation
        const db = await initializeDB();
        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        await store.put(newRecord);
        await tx.done;
        window.dispatchEvent(new Event('jhaUpdated'));

        navigate('/jha/step-1', { state: { isNew: false, jha: newRecord } });
    }

    async function handleDeleteRecord() {
        if (!deleteDialog.item) return;

        try {
            const db = await initializeDB();
            const tx = db.transaction(JHA_STORE, 'readwrite');
            const store = tx.objectStore(JHA_STORE);
            await store.delete(deleteDialog.item.id);
            await tx.done;

            // Reload data
            await loadData();
            setDeleteDialog({ open: false, item: null });

            // Dispatch event
            window.dispatchEvent(new Event('jhaUpdated'));
        } catch (error) {
            console.error('Error deleting JHA:', error);
        }
    }

    async function handleSendEmail(item, e) {
        e.stopPropagation();

        // Check if email was already sent
        if (item.emailSent) {
            await Swal.fire({
                icon: 'info',
                title: 'Email Already Sent',
                text: 'Email has already been sent for this JHA'
            });
            return;
        }

        // Check cooldown (1 hour)
        const now = Date.now();
        const cooldownKey = item.localId || item.id;
        const lastSentTime = emailCooldowns[cooldownKey];

        if (lastSentTime && (now - lastSentTime) < 60 * 60 * 1000) {
            const remainingMinutes = Math.ceil((60 * 60 * 1000 - (now - lastSentTime)) / 1000 / 60);
            await Swal.fire({
                icon: 'warning',
                title: 'Please Wait',
                text: `You can send another email in ${remainingMinutes} minutes`
            });
            return;
        }

        const result = await Swal.fire({
            icon: 'question',
            title: 'Send Email?',
            text: 'Do you really want to receive an email with all the data from this JHA to your email address?',
            showCancelButton: true,
            confirmButtonText: 'Yes, Send',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33'
        });

        if (!result.isConfirmed) {
            return;
        }

        setSendingEmail(prev => ({ ...prev, [item.id]: true }));

        try {
            const jhaId = item.localId || item.id;
            const response = await axiosInstance.post(`/jha/local/${jhaId}/send-email`);
            const data = response.data;

            if (data.success) {
                // Update cooldown
                setEmailCooldowns(prev => ({ ...prev, [cooldownKey]: now }));

                // Update local record
                const db = await initializeDB();
                const tx = db.transaction(JHA_STORE, 'readwrite');
                const store = tx.objectStore(JHA_STORE);
                const record = await store.get(item.id);
                if (record) {
                    record.emailSent = true;
                    record.emailSentAt = data.sentAt;
                    record.emailSentTo = data.sentTo;
                    await store.put(record);
                }
                await tx.done;

                await Swal.fire({
                    icon: 'success',
                    title: 'Email Sent!',
                    text: `Email sent successfully to ${data.sentTo}`
                });
                await loadData();
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: data.message || data.error || 'Failed to send email'
                });
            }
        } catch (error) {
            console.error('Error sending email:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to send email';
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage
            });
        } finally {
            setSendingEmail(prev => ({ ...prev, [item.id]: false }));
        }
    }

    async function handleManualSync() {
        setSyncing(true);
        try {
            await jhaSyncService.syncPendingJhas();
            await loadData(); // Reload to show updated sync status
        } catch (error) {
            console.error('Manual sync failed:', error);
        } finally {
            setSyncing(false);
        }
    }

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <Skeleton variant="rectangular" height={60} />
                    <Skeleton variant="rectangular" height={200} />
                    <Skeleton variant="rectangular" height={200} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={{
            position: 'fixed',
            top: '64px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#f5f5f5'
        }}>
            {/* Simple Header */}
            <Box sx={{
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                p: 2,
                flex: '0 0 auto'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight="600">
                            JHA
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                            (Last & Current Week)
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {/* Sync button */}
                        {navigator.onLine && (
                            <Tooltip title="Sync pending JHAs">
                                <IconButton
                                    onClick={handleManualSync}
                                    disabled={syncing}
                                    sx={{ color: '#2196f3' }}
                                >
                                    {syncing ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                        <SyncIcon />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                            sx={{
                                backgroundColor: '#4caf50',
                                '&:hover': { backgroundColor: '#45a049' }
                            }}
                        >
                            New
                        </Button>
                    </Stack>
                </Box>

                {/* Simple Filter Buttons */}
                <ToggleButtonGroup
                    value={filterStatus}
                    exclusive
                    onChange={(e, newValue) => newValue && setFilterStatus(newValue)}
                    fullWidth
                    size="small"
                >
                    <ToggleButton value="all">
                        All ({statusCounts.total || 0})
                    </ToggleButton>
                    {Object.entries(statusConfig).map(([key, config]) => {
                        const count = statusCounts[key] || 0;
                        if (count === 0) return null;

                        return (
                            <ToggleButton
                                key={key}
                                value={key}
                                sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: config.color,
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: config.color
                                        }
                                    }
                                }}
                            >
                                {config.label} ({count})
                            </ToggleButton>
                        );
                    })}
                </ToggleButtonGroup>
            </Box>

            {/* Content */}
            <Box sx={{
                flex: '1 1 auto',
                overflow: 'auto',
                p: 2
            }}>
                {filteredList.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                            No records found
                        </Typography>
                    </Card>
                ) : (
                    <Stack spacing={1}>
                        {filteredList.map((item, index) => {
                            const normalizedStatus = item.status === 'completed' ? 'finish' : item.status;
                            const config = statusConfig[normalizedStatus] || statusConfig.draft;
                            const StatusIcon = config.icon;
                            
                            const dateString = item.stepOne?.date || item.stepTwo?.date;
                            const itemDate = dateString
                                ? parseLocalDate(dateString)
                                : (item.createdAt ? new Date(item.createdAt) : new Date());
                            
                            const now = new Date();
                            const isCurrentWeek = isSameWeek(itemDate, now, { weekStartsOn: 1 });
                            
                            // Check if we need to show week separator
                            const prevItem = index > 0 ? filteredList[index - 1] : null;
                            const prevDateString = prevItem?.stepOne?.date || prevItem?.stepTwo?.date;
                            const prevDate = prevItem
                                ? (prevDateString
                                    ? parseLocalDate(prevDateString)
                                    : (prevItem.createdAt ? new Date(prevItem.createdAt) : new Date()))
                                : null;
                            
                            const showWeekSeparator = index === 0 || 
                                (prevDate && !isSameWeek(itemDate, prevDate, { weekStartsOn: 1 }));
                            
                            return (
                                <React.Fragment key={item.id}>
                                    {showWeekSeparator && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            my: 1,
                                            px: 1
                                        }}>
                                            <Divider sx={{ flex: 1 }} />
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                {isCurrentWeek ? 'This Week' : 'Last Week'}
                                            </Typography>
                                            <Divider sx={{ flex: 1 }} />
                                        </Box>
                                    )}
                                <Card
                                    key={item.id}
                                    sx={{
                                        p: 2,
                                        cursor: 'pointer',
                                        '&:active': {
                                            backgroundColor: '#f5f5f5'
                                        }
                                    }}
                                    onClick={() => handleSelectRecord(item)}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {/* Icon */}
                                        <StatusIcon sx={{ 
                                            fontSize: 32,
                                            color: config.color 
                                        }} />

                                        {/* Content */}
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle1" fontWeight="600">
                                                {(() => {
                                                    const jobNumber = item.stepOne?.jobNumber || item.stepTwo?.jobNumber || '';
                                                    const locationName = item.stepOne?.locationName || item.stepTwo?.locationName || '';
                                                    const displayValue = jobNumber || locationName || 'No Job';
                                                    const dayOfWeek = itemDate ? format(itemDate, 'EEEE') : '';
                                                    const formattedDate = itemDate ? format(itemDate, 'MM/dd/yyyy') : '';
                                                    return `JHA (${displayValue}) ${dayOfWeek} ${formattedDate}`;
                                                })()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.stepOne?.jobSiteName
                                                    || item.stepTwo?.jobSiteName
                                                    || item.stepOne?.locationName
                                                    || item.stepTwo?.locationName
                                                    || 'No site name'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                <Chip
                                                    label={config.label}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: config.color,
                                                        color: 'white',
                                                        height: 20,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />
                                                {/* Sync status indicator */}
                                                {item.syncStatus && (
                                                    <Tooltip title={
                                                        item.syncStatus === 'synced'
                                                            ? 'Synced to server'
                                                            : 'Pending sync'
                                                    }>
                                                        {item.syncStatus === 'synced' ? (
                                                            <CloudIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                                                        ) : (
                                                            <CloudOffIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                                                        )}
                                                    </Tooltip>
                                                )}
                                                {/* Initial report pending indicator */}
                                                {item.stepThree?.initialSubmission?.status === 'pending' && (
                                                    <Tooltip title="Initial report pending - will send when online">
                                                        <Chip
                                                            label="Initial Pending"
                                                            size="small"
                                                            icon={<CloudOffIcon sx={{ fontSize: 14 }} />}
                                                            sx={{
                                                                height: 20,
                                                                fontSize: '0.7rem',
                                                                backgroundColor: '#ff9800',
                                                                color: 'white',
                                                                '& .MuiChip-icon': {
                                                                    color: 'white'
                                                                }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Actions */}
                                        <Stack direction="row" spacing={1.5}>
                                            {(normalizedStatus === 'finish') && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => handleCopyRecord(item, e)}
                                                        sx={{ color: '#666' }}
                                                    >
                                                        <CopyIcon fontSize="small" />
                                                    </IconButton>
                                                    {item.emailSent ? (
                                                        <Tooltip title={`Email sent on ${new Date(item.emailSentAt).toLocaleString()}`}>
                                                            <IconButton
                                                                size="small"
                                                                disabled
                                                                sx={{ color: '#4caf50' }}
                                                            >
                                                                <EmailSentIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleSendEmail(item, e)}
                                                            disabled={sendingEmail[item.id] || (() => {
                                                                const cooldownKey = item.localId || item.id;
                                                                const lastSentTime = emailCooldowns[cooldownKey];
                                                                return lastSentTime && (Date.now() - lastSentTime) < 60 * 60 * 1000;
                                                            })()}
                                                            sx={{ color: '#ff9800' }}
                                                        >
                                                            {sendingEmail[item.id] ? (
                                                                <CircularProgress size={20} />
                                                            ) : (
                                                                <EmailIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    )}
                                                </>
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDeleteDialog({ open: true, item });
                                                }}
                                                sx={{ color: '#f44336' }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Card>
                                </React.Fragment>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteDialog.open} 
                onClose={() => setDeleteDialog({ open: false, item: null })}
            >
                <DialogTitle>Delete JHA?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete JHA{' '}
                        <strong>
                            {deleteDialog.item?.stepOne?.jobNumber ||
                             deleteDialog.item?.stepTwo?.jobNumber ||
                             deleteDialog.item?.stepOne?.locationName ||
                             deleteDialog.item?.stepTwo?.locationName ||
                             'this record'}
                        </strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDeleteDialog({ open: false, item: null })}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteRecord}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
